/**
 * Zonas — o mapa de operação da Dozy.
 *
 * O catálogo de Luanda está todo carregado, mas quase todo desligado. Este
 * ecrã é o interruptor: hoje só o Zango 8000 e o Kilamba estão ligados,
 * amanhã liga-se o Sequele, e nada disto precisa de um deploy.
 *
 * Desligar não apaga nada. Os trabalhadores que lá estão continuam a
 * trabalhar; o que a zona deixa de fazer é receber gente nova. É assim que
 * se recua de uma zona sem perder o que já lá foi construído.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { MapPin, Loader2, Plus, Users, Power, X } from 'lucide-react';
import api from '../utils/api';
import { useToast } from '../hooks/useToast';

const ZonesPage = () => {
  const { success, error } = useToast();
  const [zonas, setZonas] = useState(null);
  const [nova, setNova] = useState(null);
  const [busy, setBusy] = useState(false);

  const carregar = useCallback(() => {
    api.get('/operations/zones')
      .then((r) => setZonas(r.data || []))
      .catch(() => { setZonas([]); error('Erro ao carregar as zonas'); });
  }, [error]);
  useEffect(() => { carregar(); }, [carregar]);

  const alternar = async (z) => {
    if (z.activa && z.trabalhadores > 0
      && !window.confirm(
        `Desligar ${z.nome}?\n\nOs ${z.trabalhadores} profissionais já registados continuam a trabalhar. `
        + 'A zona é que deixa de receber gente nova.')) return;
    try {
      await api.put(`/operations/zones/${z.zone_id}`, { activa: !z.activa });
      success(`${z.nome} ${z.activa ? 'desligada' : 'ligada'}.`);
      carregar();
    } catch (e) { error(e.response?.data?.message || 'Não foi possível mudar a zona.'); }
  };

  const criar = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post('/operations/zones', nova);
      setNova(null);
      carregar();
    } catch (err) {
      error(err.response?.data?.message || 'Não foi possível criar a zona.');
    } finally { setBusy(false); }
  };

  const activas = (zonas || []).filter((z) => z.activa);
  const desligadas = (zonas || []).filter((z) => !z.activa);

  const Cartao = ({ z }) => (
    <li className={`p-3 rounded-xl border flex items-center gap-3 flex-wrap
      ${z.activa ? 'border-slate-700 bg-slate-900' : 'border-slate-800 bg-slate-900/40'}`}>
      <span className={`grid place-items-center w-10 h-10 rounded-lg shrink-0
        ${z.activa ? 'bg-blue-500/15 text-blue-400' : 'bg-slate-800 text-slate-600'}`}>
        <MapPin className="w-5 h-5" />
      </span>

      <div className="flex-1 min-w-[140px]">
        <div className={`font-semibold ${z.activa ? 'text-slate-100' : 'text-slate-400'}`}>
          {z.nome}
        </div>
        <div className="text-xs text-slate-500">
          <span className="font-mono">{z.codigo}</span>
          {z.municipio && <> · {z.municipio}</>}
        </div>
      </div>

      {/* Só faz sentido mostrar contagem onde há gente. */}
      {z.trabalhadores > 0 && (
        <span className="flex items-center gap-1.5 text-xs text-slate-400">
          <Users className="w-3.5 h-3.5" />
          {z.activos}/{z.trabalhadores} activos
        </span>
      )}

      <button
        onClick={() => alternar(z)}
        aria-pressed={z.activa}
        className={`inline-flex items-center gap-1.5 px-3 min-h-[44px] rounded-lg text-sm font-medium
          ${z.activa
            ? 'bg-green-500/15 text-green-400 hover:bg-green-500/25'
            : 'bg-slate-800 text-slate-400 hover:bg-slate-700'}`}
      >
        <Power className="w-4 h-4" />
        {z.activa ? 'Ligada' : 'Desligada'}
      </button>
    </li>
  );

  return (
    <div className="space-y-5">
      <div className="flex items-start gap-3 flex-wrap">
        <div className="flex-1 min-w-[220px]">
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <MapPin className="w-6 h-6 text-blue-400" /> Zonas
          </h1>
          <p className="text-sm text-slate-400 mt-1">
            O catálogo de Luanda está carregado. Ligue uma zona quando a operação lá chegar.
          </p>
        </div>
        <button
          onClick={() => setNova({ codigo: '', nome: '', municipio: '', activa: false })}
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-500 text-white font-semibold"
        >
          <Plus className="w-4 h-4" /> Nova zona
        </button>
      </div>

      {zonas === null ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-slate-500" /></div>
      ) : (
        <>
          <section>
            <h2 className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">
              A operar ({activas.length})
            </h2>
            {activas.length === 0 ? (
              <p className="text-sm text-amber-400 py-3">
                Nenhuma zona ligada — não é possível cadastrar profissionais.
              </p>
            ) : (
              <ul className="space-y-2">{activas.map((z) => <Cartao key={z.zone_id} z={z} />)}</ul>
            )}
          </section>

          <section>
            <h2 className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">
              Prontas a ligar ({desligadas.length})
            </h2>
            <ul className="space-y-2">{desligadas.map((z) => <Cartao key={z.zone_id} z={z} />)}</ul>
          </section>
        </>
      )}

      {nova && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
          onClick={() => !busy && setNova(null)}>
          <form onSubmit={criar} onClick={(e) => e.stopPropagation()}
            className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-md p-5 space-y-4">
            <div className="flex items-start gap-3">
              <h2 className="text-lg font-bold text-white flex-1">Nova zona</h2>
              <button type="button" onClick={() => setNova(null)} aria-label="Fechar"
                className="grid place-items-center w-11 h-11 rounded-xl bg-slate-800 text-slate-400">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div>
              <label htmlFor="z-nome" className="block text-sm text-slate-400 mb-1">Nome</label>
              <input id="z-nome" required value={nova.nome}
                onChange={(e) => setNova((n) => ({ ...n, nome: e.target.value }))}
                className="w-full px-3 py-2.5 rounded-lg bg-slate-800 border border-slate-700 text-slate-200 outline-none focus:border-blue-500" />
            </div>

            <div>
              <label htmlFor="z-cod" className="block text-sm text-slate-400 mb-1">Código</label>
              <input id="z-cod" required value={nova.codigo} maxLength={5}
                onChange={(e) => setNova((n) => ({ ...n, codigo: e.target.value.toUpperCase() }))}
                className="w-full px-3 py-2.5 rounded-lg bg-slate-800 border border-slate-700 text-slate-200 font-mono outline-none focus:border-blue-500" />
              {/* Isto não é um detalhe cosmético: o código fica colado a cada
                  trabalhador que a zona registar, e depois não se muda. */}
              <p className="text-xs text-slate-500 mt-1.5">
                2 a 5 caracteres, a começar por letra. Entra no cartão de cada profissional
                (<span className="font-mono">DOZY-{nova.codigo || 'XXX'}-0001</span>) e não pode ser mudado depois.
              </p>
            </div>

            <div>
              <label htmlFor="z-mun" className="block text-sm text-slate-400 mb-1">Município (opcional)</label>
              <input id="z-mun" value={nova.municipio}
                onChange={(e) => setNova((n) => ({ ...n, municipio: e.target.value }))}
                className="w-full px-3 py-2.5 rounded-lg bg-slate-800 border border-slate-700 text-slate-200 outline-none focus:border-blue-500" />
            </div>

            <label className="flex items-center gap-3 min-h-[44px] px-3 rounded-lg bg-slate-800 cursor-pointer">
              <input type="checkbox" checked={nova.activa}
                onChange={(e) => setNova((n) => ({ ...n, activa: e.target.checked }))}
                className="w-4 h-4 accent-blue-500" />
              <span className="text-sm text-slate-300">Ligar já esta zona</span>
            </label>

            <div className="flex gap-2 pt-1">
              <button type="button" onClick={() => setNova(null)} disabled={busy}
                className="px-4 py-2.5 rounded-lg bg-slate-800 text-slate-300">Cancelar</button>
              <button type="submit" disabled={busy}
                className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-blue-600 text-white font-semibold disabled:opacity-50">
                {busy && <Loader2 className="w-4 h-4 animate-spin" />} Criar zona
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default ZonesPage;
